import cn from "classnames";
import Address from "components/shared/address";
import ForwardingSource from "components/shared/forwardingSource";
import Loader from "components/shared/loader";
import Phonenumber from "components/shared/phonenumber";
import { formatDateTimezone, getShortAddress } from "helpers/bidpoint";
import { dateTimeToView } from "helpers/date";
import { ORDER_PRICE_TYPE_ENUM } from "helpers/forwarding";
import { paths } from "helpers/paths";
import { formatNumber } from "helpers/system";
import upperFirst from "lodash/upperFirst";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { cargorunOrderTruckMatchings } from "sources/forwarding";

const AddressWithTime = ({ point }) => {
    const time = dateTimeToView(point.planEnterTime);
    const utcTimezone = formatDateTimezone({ planEnterDateOffset: point.planEnterTime });
    const address = getShortAddress(point.location);
    return (
        <>
            <span className="clearfix">
                <Address address={address} />
            </span>
            <small className="text-muted">
                {time} {utcTimezone}
            </small>
        </>
    );
};

const Item = (props) => {
    const {
        id,
        index,
        lastPoint,
        firstPoint,
        emptyMileage,
        outputWithEmptyMileage,
        price,
        orderPriceType,
        contactPersonName,
        contactPersonPhoneNumber,
        organization,
        organizationId,
        organizationName,
        organizationHasLogo,
        documentsLink,
        outputWithoutEmptyMileage,
        orderId,
    } = props;
    return (
        <tr>
            <td>{index + 1}</td>
            <td>
                {
                    // eslint-disable-next-line react/jsx-no-target-blank
                    <a
                        target="_blank"
                        rel="noopener"
                        href={`${paths.forwardingLogin}?serverKey=${window.location.hostname}&token=${localStorage.getItem(
                            "token",
                        )}&redirectUrl=%2FtransporterOrders%2F${orderId}?matchingId=${id}`}
                    >
                        {id}
                    </a>
                }
            </td>
            <td>
                <AddressWithTime point={firstPoint} />
            </td>
            <td>
                <AddressWithTime point={lastPoint} />
            </td>
            <td>{formatNumber(outputWithoutEmptyMileage) || "-"}</td>
            <td>{formatNumber(emptyMileage) || "-"}</td>
            <td>{formatNumber(outputWithEmptyMileage) || "-"}</td>
            <td>
                {price
                    ? formatNumber(price, { style: "currency", currency: organization?.currency })
                    : "Стоимость не указана"}
                {orderPriceType && (
                    <div>
                        <span
                            className={cn("badge badge-pill", {
                                "badge-primary": orderPriceType === 10,
                                "badge-info": orderPriceType === 20,
                                "badge-success": orderPriceType === 30,
                            })}
                        >
                            {ORDER_PRICE_TYPE_ENUM[orderPriceType]}
                        </span>
                    </div>
                )}
                {contactPersonName && <div>{contactPersonName}</div>}
                <Phonenumber phone={contactPersonPhoneNumber} />
            </td>
            <td className="b-table__source-cell">
                <ForwardingSource
                    name={organizationName}
                    knowledgeBaseUrl={documentsLink}
                    iconUrl={organizationHasLogo ? paths.forwardingOrganizationLogo(organizationId) : undefined}
                />
            </td>
            <td>
                <div className="d-flex">
                    {
                        // eslint-disable-next-line react/jsx-no-target-blank
                        <a
                            className="btn btn-brand mr-2 btn-sm"
                            target="_blank"
                            rel="noopener"
                            href={`${paths.forwardingLogin}?serverKey=${window.location.hostname}&token=${localStorage.getItem(
                                "token",
                            )}&redirectUrl=%2FtransporterOrders%2F${orderId}?matchingId=${id}`}
                        >
                            Подробнее
                        </a>
                    }
                </div>
            </td>
        </tr>
    );
};

const ExternalDistributionBid = ({ carId, close, organization }) => {
    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [activeSort, changeSort] = useState({ column: "outputWithEmptyMileage", value: "desc" });
    const [needSort, setNeedSort] = useState(true);

    const sortedItems = (data) => {
        return data.sort((a, b) => {
            const itemValue = (row) => {
                const plainValue = activeSort.column.split(".").reduce((o, k) => (o ? o[k] : null), row);
                return activeSort.column.includes("planEnterTime") ? moment(plainValue) : plainValue;
            };
            if (activeSort.value === "asc") {
                return itemValue(a) - itemValue(b);
            } else {
                return itemValue(b) - itemValue(a);
            }
        });
    };

    useEffect(() => {
        setIsLoading(true);
        cargorunOrderTruckMatchings(carId).then((res) => {
            if (res?.data) {
                setItems(sortedItems(res.data));
            }
            setIsLoading(false);
        });
    }, []);

    useEffect(() => {
        if (needSort) {
            setItems(sortedItems(items));
            setIsLoading(false);
            setNeedSort(false);
        }
    }, [needSort]);

    const sortableHeader = (column, title) => {
        return (
            <th className={headerClass(column)} onClick={sortByColumn.bind(this, column)}>
                {title}
                <small className="ml-2">
                    <i className="icon-uEA68-sort-solid" />
                </small>
            </th>
        );
    };

    const sortByColumn = (column) => {
        const direction = activeSort.column === column && activeSort.value === "asc" ? "desc" : "asc";
        changeSort({ column, value: direction });
        setIsLoading(true);
        setNeedSort(true);
    };

    const headerClass = (headerName) => {
        const { column, value } = activeSort;
        return cn("b-table__header-cell", {
            "b-table__header-cell--asc": column === headerName && value === "asc",
            "b-table__header-cell--desc": column === headerName && value === "desc",
        });
    };

    return (
        <div className="modal__window modal-window modal-window--wide">
            <div className="page-book__list book-list list">
                <div className="modal-window__header">
                    <div className="modal-window__header-title">Внешние заказы</div>
                    <div className="close close--sm" onClick={() => close()} />
                </div>
                {isLoading && (
                    <div className="loader-snippet--disable-loader">
                        <Loader />
                    </div>
                )}

                <table className="table table-striped table-sm">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>ID</th>
                            {sortableHeader("firstPoint.planEnterTime", "Начало")}
                            {sortableHeader("lastPoint.planEnterTime", "Конец")}
                            {sortableHeader(
                                "outputWithoutEmptyMileage",
                                <span className="text-nowrap">{upperFirst(organization?.currencyData?.name)}/км</span>,
                            )}
                            {sortableHeader("emptyMileage", "Порожний пробег, км")}
                            {sortableHeader(
                                "outputWithEmptyMileage",
                                `${upperFirst(organization?.currencyData?.name)}/км с порожним`,
                            )}
                            {sortableHeader("price", "Стоимость, контрагент")}
                            <th className="b-table__header-cel">Источник</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 && (
                            <tr>
                                <td colSpan={9} className="text-center py-4">
                                    Нет данных для отображения
                                </td>
                            </tr>
                        )}
                        {items.map((item, index) => (
                            <Item key={index} index={index} {...item} organization={organization} />
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ExternalDistributionBid;
